import { useState } from "react";
import { JSONTree } from "react-json-tree";
import { Link } from "react-router-dom";
import { Text } from "../../sudokit/components/Text";
import { WithReset } from "../../sudokit/Layout/BoxReset";
import { sendAppEvent } from "../../Utils.js";

type ToolExecution = {
  name: string;
  displayName?: string;
  arguments: Record<string, any>;
  summary?: string;
};

type ThinkingTask = {
  id: string;
  icon: string;
  title: string;
  status?: string;
  explanation?: string;
};

type ThinkingStep = {
  id: string;
  title: string;
  tasks: ThinkingTask[];
  status?: string;
};

type MessageMetadata = {
  requestStageTimings?: Record<string, number | null>;
  agenticChat?: boolean;
  agenticModelId?: string;
  agenticModelKey?: string;
  mode?: "write" | "chat";
  toolCount?: number;
  outlineUpdateCount?: number;
  toolExecutions?: ToolExecution[];
  thinkingSteps?: ThinkingStep[];
  contextElements?: string[];
  mentionedEntities?: Array<{ type: string; name: string }>;
  passage?: string;
  quickReply?: boolean;
  invocation_id?: string;
  invocationId?: string;
};

type Message = {
  id: string;
  role: string;
  content: string;
  metadata: MessageMetadata | null;
  createdAt: string;
  billing?: {
    provider: string | null;
    subprovider: string | null;
    costUsd: number | null;
  } | null;
};

type Props = {
  message: Message;
  isBranchedMessage?: boolean;
  highlighted?: boolean;
  userBranchSiblingIndex?: number;
  userBranchSiblingCount?: number;
  onUserBranchPrev?: () => void;
  onUserBranchNext?: () => void;
};

const JSON_TREE_THEME = {
  scheme: "light",
  base00: "#00000000",
  base01: "#223b54",
  base02: "#ea580c",
  base03: "#6b7280",
  base04: "#7e8d9a",
  base05: "#374151",
  base06: "#1f2937",
  base07: "#111827",
  base08: "#dc2626",
  base09: "#ea580c",
  base0A: "#d97706",
  base0B: "#16a34a",
  base0C: "#0891b2",
  base0D: "#2563eb",
  base0E: "#9333ea",
  base0F: "#be185d",
};

function ExpandableSection({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden text-left">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 bg-gray-50 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 flex items-center justify-between"
      >
        <span>{title}</span>
        <span className="text-gray-400">{isOpen ? "▼" : "▶"}</span>
      </button>
      {isOpen && <div className="p-3 bg-white text-left">{children}</div>}
    </div>
  );
}

export function AgenticMessageCard({
  message,
  isBranchedMessage = false,
  highlighted = false,
  userBranchSiblingIndex,
  userBranchSiblingCount,
  onUserBranchPrev,
  onUserBranchNext,
}: Props) {
  const [showFullContent, setShowFullContent] = useState(false);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">("idle");
  const isUser = message.role === "user";
  const metadata = message.metadata || {};
  const hasToolExecutions = metadata.toolExecutions && metadata.toolExecutions.length > 0;
  const hasThinkingSteps = metadata.thinkingSteps && metadata.thinkingSteps.length > 0;
  const hasMetadata = metadata.agenticChat || Object.keys(metadata).length > 0;

  const formatTimestamp = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const contentPreviewLength = 300;
  const shouldTruncate = message.content.length > contentPreviewLength;
  const displayContent = showFullContent || !shouldTruncate ? message.content : message.content.slice(0, contentPreviewLength) + "...";
  const hasBranchPager =
    message.role === "user" &&
    userBranchSiblingCount != null &&
    userBranchSiblingCount > 1 &&
    userBranchSiblingIndex != null;
  const resetCopyStatusSoon = () => setTimeout(() => setCopyStatus("idle"), 2000);
  const handleCopyMessage = async () => {
    const messageLength = message.content?.length || 0;
    try {
      await navigator.clipboard.writeText(message.content || "");
      sendAppEvent("Admin Chat - Copy User Message", {
        status: "success",
        message_length: messageLength,
      });
      setCopyStatus("copied");
      resetCopyStatusSoon();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error || "Unknown error");
      sendAppEvent("Admin Chat - Copy User Message", {
        status: "failure",
        message_length: messageLength,
        error: errorMessage.slice(0, 120),
      });
      setCopyStatus("failed");
      resetCopyStatusSoon();
      // Keep this non-blocking for admin inspection workflows.
      console.error("Failed to copy message content", error);
    }
  };

  return (
    <WithReset
      id={`admin-agentic-message-${message.id}`}
      data-message-id={message.id}
      className={`rounded-lg border p-4 text-left scroll-mt-4 ${
        isUser ? "bg-blue-50 border-blue-200" : "bg-white border-gray-200"
      }${highlighted ? " ring-2 ring-amber-400 border-amber-400" : ""}`}
    >
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${isUser ? "bg-blue-200 text-blue-800" : "bg-gray-200 text-gray-800"}`}>
            {message.role}
          </span>
          {highlighted && (
            <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-medium">
              Focused
            </span>
          )}
          {metadata.quickReply && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">Quick Reply</span>
          )}
          {metadata.agenticModelKey && (
            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">{metadata.agenticModelKey}</span>
          )}
          {message.billing?.provider && (
            <span className="px-2 py-1 bg-cyan-100 text-cyan-800 rounded text-xs font-medium">
              {message.billing.provider}
            </span>
          )}
          {message.billing?.subprovider && (
            <span className="px-2 py-1 bg-sky-100 text-sky-800 rounded text-xs font-medium">
              {message.billing.subprovider}
            </span>
          )}
          {message.billing?.costUsd != null && (
            <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded text-xs font-mono font-medium">
              ${message.billing.costUsd.toFixed(4)}
            </span>
          )}
          {metadata.mode && (
            <span className={`px-2 py-1 rounded text-xs font-medium ${metadata.mode === "write" ? "bg-green-100 text-green-800" : "bg-indigo-100 text-indigo-800"}`}>
              {metadata.mode}
            </span>
          )}
          {isBranchedMessage && (
            <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-medium">
              branched
            </span>
          )}
          {metadata.toolCount !== undefined && metadata.toolCount > 0 && (
            <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium">{metadata.toolCount} tools</span>
          )}
          {metadata.outlineUpdateCount !== undefined && metadata.outlineUpdateCount > 0 && (
            <span className="px-2 py-1 bg-teal-100 text-teal-800 rounded text-xs font-medium">{metadata.outlineUpdateCount} outline updates</span>
          )}
          {(metadata.invocation_id || metadata.invocationId) && (
            <Link
              to={`/admin/invocation/${metadata.invocation_id || metadata.invocationId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium hover:bg-blue-200 hover:underline transition-colors"
              title={`View invocation ${metadata.invocation_id || metadata.invocationId}`}
            >
              Invocation →
            </Link>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <Text size="xs" color="grey-2" font="ibm-plex-mono" className="whitespace-nowrap" align="left">
            {formatTimestamp(message.createdAt)}
          </Text>
          {isUser && (
            <button
              type="button"
              onClick={handleCopyMessage}
              aria-label="Copy user message"
              aria-live="polite"
              className="px-2 py-1 rounded border border-blue-200 bg-white text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors"
            >
              {copyStatus === "copied" ? "Copied!" : copyStatus === "failed" ? "Copy failed" : "Copy"}
            </button>
          )}
        </div>
      </div>

      {!isUser && (metadata.requestStageTimings || metadata.toolCount != null) && (
        <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 rounded bg-gray-50 px-3 py-2 text-xs text-gray-600" aria-label="Turn performance">
          {([
            ["Total", metadata.requestStageTimings?.totalRequestMs],
            ["First response", metadata.requestStageTimings?.firstVisibleResponseFromRequestMs],
            ["Agent loop", metadata.requestStageTimings?.totalLoopDurationMs],
            ["First model call", metadata.requestStageTimings?.firstModelCallFromRequestMs],
          ] as const).map(([label, ms]) => typeof ms === "number" && Number.isFinite(ms) && ms >= 0
            ? <span key={label}>{label}: {(ms / 1000).toFixed(1)}s</span> : null)}
          {typeof metadata.toolCount === "number" && <span>Tools: {metadata.toolCount}</span>}
        </div>
      )}

      {metadata.passage && (
        <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-left">
          <Text size="sm" weight="medium" color="warning" align="left">Selected Passage:</Text>
          <Text size="sm" color="grey-1" align="left" className="mt-1 italic">
            &quot;{metadata.passage.slice(0, 200)}{metadata.passage.length > 200 ? "..." : ""}&quot;
          </Text>
        </div>
      )}

      <div className="mb-3 text-left">
        <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans text-left">{displayContent}</pre>
        {shouldTruncate && (
          <button
            onClick={() => setShowFullContent(!showFullContent)}
            className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium text-left"
          >
            {showFullContent ? "Show less" : "Show more"}
          </button>
        )}
      </div>

      {hasBranchPager && (
        <div className="mb-3 flex items-center gap-2 text-left">
          <button
            type="button"
            className="px-2 py-1 rounded border border-gray-300 bg-white text-xs font-medium hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!onUserBranchPrev}
            onClick={() => onUserBranchPrev?.()}
            aria-label="Previous branch thread"
          >
            Prev
          </button>
          <Text size="xs" color="grey-1" align="left">
            Thread {userBranchSiblingIndex + 1} of {userBranchSiblingCount}
          </Text>
          <button
            type="button"
            className="px-2 py-1 rounded border border-gray-300 bg-white text-xs font-medium hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!onUserBranchNext}
            onClick={() => onUserBranchNext?.()}
            aria-label="Next branch thread"
          >
            Next
          </button>
        </div>
      )}

      {(metadata.contextElements && metadata.contextElements.length > 0) && (
        <div className="mb-3 flex flex-wrap gap-1 items-center text-left">
          <Text size="xs" color="grey-2" align="left" className="mr-1">Context:</Text>
          {metadata.contextElements.map((elem, i) => (
            <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
              {elem}
            </span>
          ))}
        </div>
      )}

      {(metadata.mentionedEntities && metadata.mentionedEntities.length > 0) && (
        <div className="mb-3 flex flex-wrap gap-1 items-center text-left">
          <Text size="xs" color="grey-2" align="left" className="mr-1">Mentions:</Text>
          {metadata.mentionedEntities.map((entity, i) => (
            <span key={i} className="px-2 py-0.5 bg-pink-100 text-pink-700 rounded text-xs">
              @{entity.name} ({entity.type})
            </span>
          ))}
        </div>
      )}

      {!isUser && (metadata.invocation_id || metadata.invocationId) && (
        <div className="mb-3 p-2 bg-gray-50 border border-gray-200 rounded text-left">
          <div className="flex items-center gap-2">
            <Text size="xs" color="grey-2" align="left" className="font-medium">Invocation ID:</Text>
            <code className="text-xs font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded select-all">
              {metadata.invocation_id || metadata.invocationId}
            </code>
            <Link
              to={`/admin/invocation/${metadata.invocation_id || metadata.invocationId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
            >
              View →
            </Link>
          </div>
        </div>
      )}

      {!isUser && (hasToolExecutions || hasThinkingSteps || hasMetadata) && (
        <div className="space-y-2 mt-4 text-left">
          {hasToolExecutions && (
            <ExpandableSection title={`Tool Executions (${metadata.toolExecutions!.length})`}>
              <div className="space-y-3 text-left">
                {metadata.toolExecutions!.map((tool, i) => (
                  <div key={i} className="border border-gray-100 rounded p-3 bg-gray-50 text-left">
                    <div className="flex items-center gap-2 mb-2">
                      <Text size="sm" weight="bold" font="ibm-plex-mono" color="grey-0" align="left">
                        {tool.displayName || tool.name}
                      </Text>
                    </div>
                    {tool.summary && (
                      <Text size="sm" color="grey-1" align="left" className="mb-2">{tool.summary}</Text>
                    )}
                    <details className="text-xs text-left">
                      <summary className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium">Arguments</summary>
                      <pre className="mt-2 p-2 bg-gray-100 text-gray-800 rounded overflow-x-auto text-xs text-left [scrollbar-width:thin] [scrollbar-color:rgba(156,163,175,0.3)_transparent]">
                        {JSON.stringify(tool.arguments, null, 2)}
                      </pre>
                    </details>
                  </div>
                ))}
              </div>
            </ExpandableSection>
          )}

          {hasThinkingSteps && (
            <ExpandableSection title={`Thinking Steps (${metadata.thinkingSteps!.length})`}>
              <div className="space-y-2 text-left">
                {metadata.thinkingSteps!.map((step, i) => (
                  <div key={i} className="border border-gray-100 rounded p-3 bg-gray-50 text-left">
                    <Text size="sm" weight="medium" color="grey-0" align="left" className="mb-2">{step.title}</Text>
                    <div className="space-y-1 text-left">
                      {step.tasks.map((task, j) => (
                        <div key={j} className="flex items-start gap-2 text-left">
                          <span className="text-gray-400">
                            {task.icon === "brain" ? "🧠" : task.icon === "book" ? "📖" : task.icon === "search" ? "🔍" : "💡"}
                          </span>
                          <div className="text-left">
                            <Text
                              size="sm"
                              weight="medium"
                              align="left"
                              className={task.status === "complete" ? "text-green-700" : task.status === "error" ? "text-red-700" : "text-gray-700"}
                            >
                              {task.title}
                            </Text>
                            {task.explanation && (
                              <Text size="xs" color="grey-1" align="left" className="mt-0.5">{task.explanation}</Text>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </ExpandableSection>
          )}

          {hasMetadata && (
            <ExpandableSection title="Raw Metadata">
              <div className="text-sm overflow-x-auto text-left [scrollbar-width:thin] [scrollbar-color:rgba(156,163,175,0.3)_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400/30" style={{ maxHeight: "400px", overflowY: "auto" }}>
                <JSONTree
                  data={metadata}
                  theme={JSON_TREE_THEME}
                  invertTheme
                  shouldExpandNodeInitially={() => false}
                />
              </div>
            </ExpandableSection>
          )}
        </div>
      )}
    </WithReset>
  );
}
